import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Checkbox, 
  FormControlLabel, 
  Grid, 
  Typography, 
  Paper, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  InputAdornment, 
  IconButton 
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const EditRoleModal = ({ showModal, hideModal, id, data, getRoles }) => {
  const navigate = useNavigate();
  const [allPermissions, setAllPermissions] = useState([]);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleName, setRoleName] = useState(data.role_name);
  const [roleDescription, setRoleDescription] = useState(data.role_description);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    const initialSelectedPermissions = data.permissions.map(perm => perm.id);
    setSelectedPermissions(initialSelectedPermissions);
    fetchPermissions();
  }, [data]);

  const fetchPermissions = async () => {
    try {
      const response = await axios.get('/permissions');
      setAllPermissions(response.data);
      setFilteredPermissions(response.data); // Default filtered list
    } catch (error) {
      notifyError('Failed to load permissions');
    }
  };

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    setFilteredPermissions(
      allPermissions.filter(perm => 
        perm.perm_name.toLowerCase().includes(query)
      )
    );
  };

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("role_name", roleName);
      formData.append("role_description", roleDescription);
      formData.append("id", id);
      selectedPermissions.forEach(permissionId => {
        formData.append("permissions", permissionId);
      });

      await axios.post(`/role/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("d4uToken")}`,
        },
      });

      notifySuccess('Role updated successfully');
      getRoles();
      navigate('/roles');
      hideModal();
    } catch (error) {
      notifyError('Failed to update role');
      console.error(error);
    }
  };

  const notifySuccess = (message) => {
    toast.success(message, {
      position: 'top-right',
      autoClose: 2000,
      theme: 'light',
    });
  };

  const notifyError = (message) => {
    toast.error(message, {
      position: 'top-right',
      autoClose: 2000,
      theme: 'light',
    });
  };

  return (
    <>
      <Dialog 
        open={showModal} 
        onClose={hideModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">
            Edit Role
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Role Name"
                  variant="outlined"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  variant="outlined"
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Allowed Permissions
                </Typography>

                {/* Search Field */}
                <TextField
                  fullWidth
                  placeholder="Search permissions..."
                  variant="outlined"
                  value={searchQuery}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton>
                          <Search />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {/* Scrollable Permission List */}
                <Paper
                  variant="outlined"
                  sx={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    p: 2,
                  }}
                >
                  <Grid container spacing={1}>
                    {filteredPermissions.map((perm) => (
                      <Grid item lg={4} xs={5} sm={5}
                       md={4} 

                      key={perm.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(perm.id)}
                              onChange={() => handlePermissionToggle(perm.id)}
                              color="primary"
                            />
                          }
                          label={perm.perm_name}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={hideModal}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            color="primary"
            variant="contained"
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>
      
      <ToastContainer />
    </>
  );
};

export default EditRoleModal;
